/**
 * Запросы к API и их кеширование.
 *
 * Отдельного внимания требует инвалидация: §3 описывает найденную в демо
 * архитектурную ошибку — правки врача не были видны нигде, кроме раздела
 * «Врачи», потому что остальные экраны читали исходные данные напрямую.
 * На сервере одна база, и сама по себе ошибка не воспроизведётся, но кеш
 * TanStack Query воспроизведёт её точь-в-точь, если после сохранения врачей
 * не сбросить справочники. Поэтому `invalidateAfterCommit` сбрасывает
 * bootstrap ВСЕГДА, независимо от того, какой раздел сохранялся.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ColorSettings, ModuleName, PriceListModel, ThemeName, ThemePair } from '@otc/shared';
import { api, commitModule, type ModuleCommitPayload, type ModuleCommitResult } from './client.js';

// --- Типы ответов API -------------------------------------------------------

export interface CurrentUser {
  id: string;
  name: string;
  login: string;
  role: 'OWNER' | 'EMPLOYEE';
  salaryAccess: boolean;
  /**
   * Личная тема: у каждого своя. `null` — не выбирал, действует тема
   * лаборатории. Необязательно: сервер до обновления её не присылает.
   */
  themeName?: ThemeName | null;
  /** Цвета своей темы, если человек её собирал. */
  themePair?: ThemePair | null;
  pinnedOrderMonth?: string | null;
}

export interface ApiLegal {
  id: string; clinicId: string; name: string; form: string; shortForm: string;
  pay: 'По договору' | 'Наличные';
  contractNo: string; contractDate: string; contract: string; email: string;
  inn: string; ogrn: string; kpp: string; address: string;
  bank: string; bik: string; rs: string; ks: string; note: string;
  signer: string; signerPosition: string; signerName: string; signerGenitive: string;
  warnOnReady: boolean; mergeInvoiceItems: boolean;
  /** Фамилии в строке счёта: пациент (по умолчанию есть) и врач (по умолчанию нет). */
  invoicePatient: boolean; invoiceDoctor: boolean;
  /** Файлы договора: сам договор и дополнительные соглашения. */
  files: ApiContractFile[];
  version: number;
}

export interface ApiBranch {
  id: string; clinicId: string; legalId: string; name: string; version: number;
  /** Фактический адрес точки — отдельно от юридического адреса юрлица. */
  address: string;
}

export interface ApiClinic {
  id: string; name: string; version: number;
  countDelivery?: boolean;
  deliveryKop?: number;
  doctors: string[];
  branches: ApiBranch[];
  legals: ApiLegal[];
}

export interface ApiDoctor {
  id: string; name: string; clinics: string[];
  /** Полное ФИО по частям — показывается в раскрытой карточке. */
  lastName?: string; firstName?: string; middleName?: string;
  phone: string; email: string; telegram: string; whatsapp: string; max: string;
  notify: boolean; note: string; version: number;
  /** В архиве: убран из списков и выбора, остаётся в старых заказах. */
  archived: boolean;
  /** Видит ли врач цены в своём кабинете. По умолчанию нет. */
  showPrices?: boolean;
  /**
   * Кабинет врача. `null` — кабинет не открывали ИЛИ смотрит не владелец:
   * сотруднику лаборатории состояние двери наружу не показывается.
   */
  /** Просьбы врача поправить свои данные — ждут решения владельца. */
  portalRequests?: {
    id: string;
    /** phone | email | telegram | max */
    field: string;
    value: string;
    note: string;
    createdAt: string;
  }[];
  portal?: {
    login: string;
    /** Задал ли врач пароль по приглашению. */
    hasPassword: boolean;
    /** До какого момента действует выданное приглашение. */
    inviteExpires: string | null;
    blocked: boolean;
    lastLoginAt: string | null;
  } | null;
}

export interface ApiConstruction {
  id: string; type: 'Базовая конструкция' | 'Дополнительный элемент';
  categoryId: string; name: string;
  /** Описание изделия — второй столбец прайс-листа. Может быть пустым. */
  description: string;
  priceCashKop: number; priceContractKop: number; costKop: number;
  hasColor: boolean;
  /** Разрешено ли заказывать несколько одинаковых штук. */
  qtyAdjustable: boolean;
  materials: { materialId: string; qty: number }[];
  version: number;
}

export interface ApiMaterial {
  id: string; name: string; unit: string; priceKop: number; version: number;
}

export interface ApiColor {
  id: string; categoryId: string | null; name: string; art: string; hex: string;
  image: string | null; unspecified: boolean; version: number;
  /** Вид миниатюры: сплошная заливка, блёстки или разноцветный «ирис». */
  fill: 'SOLID' | 'GLITTER' | 'IRIS';
  /** Цвет крапин для блёсток; у «ириса» не используется. */
  glitterHex: string;
}

export interface ApiCategory {
  id: string; kind: 'base' | 'extra' | 'color'; name: string; sortOrder: number;
  /** Всегда 1: у категорий нет оптимистичной блокировки, см. bootstrap. */
  version: number;
}

export interface ApiEmployee {
  id: string; name: string;
  /* «Менеджер» убран 25.09.2026: такой должности в лаборатории нет. */
  position: 'Техник' | 'Администратор';
  role: 'OWNER' | 'EMPLOYEE';
  login: string; blocked: boolean; hasPassword: boolean; version: number;
  /** Ставка и премия приезжают только владельцу: финансы закрыты для остальных. */
  percent?: number;
  bonusKop?: number;
}

export interface ApiSettings {
  performer: Record<string, string> | null;
  smtp: { host: string; port: string; login: string; password: null; hasPassword: boolean } | null;
  templates: { invoiceTemplate: string; actTemplate: string; letter1: string; letter2: string };
  rentKop: number | null;
  /** Гарантированная зарплата, копейки. Пусто — значит не владелец, доступа нет. */
  minSalaryKop: number | null;
  /** Ставка налога УСН «доходы», проценты. `null` — не владелец. */
  taxPercent: number | null;
  themeName: 'teal' | 'blue' | 'gray' | 'sage' | 'rose' | 'plum' | 'coffee' | 'ink' | 'custom';
  /**
   * Настроенные цвета — только отличия от стандартных. Необязательно: сервер
   * до обновления их не присылает, и тогда стоят стандартные.
   */
  colors?: ColorSettings;
  version: number;
}

export interface Bootstrap {
  user: CurrentUser;
  clinics: ApiClinic[];
  doctors: ApiDoctor[];
  constructions: ApiConstruction[];
  materials: ApiMaterial[];
  colors: ApiColor[];
  categories: ApiCategory[];
  employees: ApiEmployee[];
  settings: ApiSettings | null;
}

export interface ApiOrder {
  id: string;
  /** Момент создания. Нужен для устойчивого порядка строк, не для показа. */
  createdAt?: string;
  doctorId: string; clinicId: string; branchId: string; constructionId: string;
  /** Сколько одинаковых аппаратов. Старые заказы приходят с единицей. */
  constructionQty: number;
  /** Челюсть — приписка к конструкции. Пусто у большинства заказов. */
  jaw: '' | 'Верхняя челюсть' | 'Нижняя челюсть' | 'Обе челюсти';
  colors: string[];
  extras: { id: string; qty: number }[];
  patient: string; received: string;
  /** `null` — срок ещё не согласован. */
  due: string | null;
  status: 'В очереди' | 'В работе' | 'Ожидание' | 'Готово';
  urgency: 'Авто' | 'Очень срочно' | 'Срочно' | 'В работе' | 'Готово';
  /** Пусто — способ оплаты ещё не определён: клиника не выбрана. */
  pay: 'По договору' | 'Наличные' | '';
  paid: boolean; costKop: number; expenseKop: number; notes: string;
  files: { id: string; storedName: string; originalName: string; size: number; mime: string }[];
  invoiceId: string | null;
  invoiceNumber: number | null;
  /**
   * Номер счёта из прежнего учёта: счёт выставлен вне программы.
   * Показывается, но не открывается и не редактируется — переносы июля
   * и августа 2026 года, дальше такие номера не появляются.
   */
  legacyInvoiceNo: number | null;
  /** Какие уведомления врачу по этому заказу уже отправлены. */
  lettersSent: ('ACCEPTED' | 'READY')[];
  version: number;
}

export interface ApiContractFile {
  id: string; originalName: string; size: number; mime: string;
  uploadedAt: string; uploadedBy: string | null;
}

export interface ApiInvoiceItem {
  id: string; position: number; name: string; qty: number; unit: string; priceKop: number;
}

export interface ApiInvoice {
  documentRequisites?: import('@otc/shared').InvoiceRequisites | null;
  id: string; number: number;
  /** Приписка к номеру: «1» в «368-1». Пусто у обычных счетов. */
  numberSuffix: string | null;
  clinicId: string; legalId: string;
  clinicName: string; legalName: string; legalShortForm: string;
  orderIds: string[];
  items: ApiInvoiceItem[];
  date: string;
  /** Сводная метка: у оплаченного счёта заслоняет собой отправку. */
  status: 'Не отправлен' | 'Отправлен' | 'Оплачено';
  /** Ушло ли письмо. Хранится в счёте. */
  sent: boolean;
  /** Подпись и печать исполнителя в счёте и в акте. */
  facsimileSchet: boolean;
  facsimileAkt: boolean;
  /** Оплачены ли ВСЕ заказы счёта. Не хранится, а считается по заказам. */
  paid: boolean;
  paidCount: number;
  orderCount: number;
  /**
   * Счёт, к заказам которого относится этот документ.
   *
   * Заполнен у второго счёта на ту же работу: свои заказы у него забрал
   * первый, и попасть к ним можно только через эту ссылку.
   */
  linkedInvoiceId: string | null;
  sentAt: string | null;
  version: number;
}

export interface ApiReportRow {
  orderId: string; doctor: string; patient: string; construction: string;
  received: string; due: string; costKop: number;
  /** Элементы работы с ценами — только у развёрнутого наряда. */
  items?: { name: string; qty: number; priceKop: number }[];
}

export interface ApiReport {
  id: string; clinicName: string; legalName: string; periodLabel: string; responsible: string;
  showReceived: boolean; showDue: boolean; showSubtotal: boolean;
  rows: ApiReportRow[]; totalKop: number; date: string;
  /** Подпись и печать исполнителя в рамке «Подпись:». */
  facsimile?: boolean;
}

// --- Ключи кеша -------------------------------------------------------------

export const queryKeys = {
  me: ['me'] as const,
  bootstrap: ['bootstrap'] as const,
  orders: (month: string | null) => ['orders', month] as const,
  invoices: ['invoices'] as const,
  reports: ['reports'] as const,
};

// --- Запросы ----------------------------------------------------------------

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.get<{ user: CurrentUser }>('/api/auth/me'),
    retry: false,
    staleTime: Infinity,
  });
}

/**
 * Как часто данные обновляются САМИ, без перезагрузки страницы.
 *
 * В лаборатории за программой сидят несколько человек сразу: один завёл
 * заказ, у второго открыт тот же журнал — и, не видя чужой записи, второй
 * заводил её повторно. Поэтому списки перечитываются тихо, в фоне.
 *
 * Полминуты — компромисс: чужой заказ появляется почти сразу, а нагрузки
 * это не создаёт (один запрос на человека в полминуты). Справочники
 * меняются куда реже, им хватает пяти минут.
 *
 * Открытые правки при этом не страдают: черновик раздела не перезаписывается,
 * пока в нём есть несохранённое (см. `useSync`).
 */
const LIVE = {
  refetchInterval: 30_000,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;

export function useBootstrap(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.bootstrap,
    queryFn: () => api.get<Bootstrap>('/api/bootstrap'),
    enabled,
    ...LIVE,
    refetchInterval: 300_000,
  });
}

/** `month` в формате `YYYY-MM`; `null` — все заказы. */
export function useOrders(month: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.orders(month),
    queryFn: () =>
      api.get<{ orders: ApiOrder[] }>(
        month === null ? '/api/orders?all=true' : `/api/orders?month=${month}`,
      ),
    enabled,
    ...LIVE,
  });
}

export function useInvoices(enabled = true) {
  return useQuery({
    queryKey: queryKeys.invoices,
    queryFn: () => api.get<{ invoices: ApiInvoice[] }>('/api/invoices'),
    enabled,
    ...LIVE,
  });
}

/** Фактическая зарплата за месяц, назначенная вручную. */
export interface ApiSalaryOverride {
  bonusKop: number;
  employeeId: string;
  /** `YYYY-MM`. */
  monthKey: string;
  /** Итог, назначенный вручную. `null` — считается по проценту. */
  amountKop: number | null;
  /** Аванс за месяц, копейки: часть той же зарплаты. */
  advanceKop: number;
  /** Отпускные за месяц, копейки: добавка сверх зарплаты. */
  vacationKop: number;
  note: string;
  updatedAt: string;
}

/**
 * Назначенные суммы за все месяцы сразу.
 *
 * Их единицы — по одной на сотрудника за месяц, — и грузить их помесячно
 * значило бы дёргать сервер при каждом шаге стрелкой по месяцам ради
 * нескольких строк.
 */
export function useSalaryOverrides(enabled = true) {
  return useQuery({
    queryKey: ['salaries'] as const,
    queryFn: () => api.get<{ salaries: ApiSalaryOverride[] }>('/api/salaries'),
    enabled,
  });
}

/** Прочие расходы месяца: оборудование, ремонт, разовые траты. */
export interface ApiMonthExpense {
  /** `YYYY-MM`. */
  monthKey: string;
  amountKop: number;
  /** За что потратили: «новый принтер». Пусто — просто сумма. */
  note: string;
  updatedAt: string;
}

/**
 * Расходы всех месяцев сразу.
 *
 * Их единицы — по одной записи на месяц, — и грузить помесячно значило бы
 * дёргать сервер при каждом шаге стрелкой по месяцам.
 */
export function useMonthExpenses(enabled = true) {
  return useQuery({
    queryKey: ['month-expenses'] as const,
    queryFn: () => api.get<{ expenses: ApiMonthExpense[] }>('/api/month-expenses'),
    enabled,
  });
}

/**
 * Своя зарплата за месяц — приходит только в ответ на пароль.
 *
 * Не запрос-подписка, а разовое действие: числа нигде не кешируются, не
 * перечитываются в фоне и не остаются в памяти программы после ухода со
 * страницы. Иначе пароль защищал бы только первый показ.
 */
export interface ApiMySalary {
  monthKey: string;
  name: string;
  percent: number;
  shareKop: number;
  minSalaryKop: number;
  atMinimum: boolean;
  bonusKop: number;
  vacationKop: number;
  advanceKop: number;
  salaryKop: number;
  remainderKop: number;
  manual: boolean;
  manualNote: string;
  computedKop: number;
  /** Итоги за полгода, включая показанный месяц: старые месяцы первыми. */
  history: { monthKey: string; salaryKop: number }[];
}

export async function fetchMySalary(password: string, monthKey: string): Promise<ApiMySalary> {
  const ответ = await api.post<{ salary: ApiMySalary }>('/api/my-salary', { password, monthKey });
  return ответ.salary;
}

export function useReports(enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports,
    queryFn: () => api.get<{ reports: ApiReport[] }>('/api/reports'),
    enabled,
  });
}

// --- Мутации ----------------------------------------------------------------

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { login: string; password: string }) =>
      api.post<{ user: CurrentUser }>('/api/auth/login', credentials),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.me, data);
      queryClient.invalidateQueries({ queryKey: queryKeys.bootstrap });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/auth/logout'),
    onSuccess: () => queryClient.clear(),
  });
}

/**
 * Сброс кеша после сохранения раздела.
 *
 * Сбрасывается ВСЁ, а не только сохранённый раздел: связи между сущностями
 * пронизывают систему насквозь. Изменение привязки врача к клинике меняет
 * список клиник, доступных в форме заказа; правка прайса меняет цены, которые
 * подставятся в следующий заказ. Точечная инвалидация здесь — источник
 * труднообъяснимых расхождений (§3).
 */
export function invalidateAfterCommit(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.bootstrap });
  queryClient.invalidateQueries({ queryKey: ['orders'] });
  queryClient.invalidateQueries({ queryKey: queryKeys.invoices });
  /*
    Записанные зарплаты тоже: удаление сотрудника или правка процента меняют
    то, что показано в расчёте рядом с фактической суммой.
  */
  queryClient.invalidateQueries({ queryKey: ['salaries'] });
}

export function useCommitModule(module: ModuleName) {
  const queryClient = useQueryClient();
  return useMutation<ModuleCommitResult, Error, ModuleCommitPayload>({
    mutationFn: (payload) => commitModule(module, payload),
    onSuccess: () => invalidateAfterCommit(queryClient),
  });
}

/**
 * Подпись и печать исполнителя для предпросмотра и печати документов.
 *
 * Отдаются сервером только вошедшим и в код программы не кладутся: файл
 * программы скачивает любой, кто открыл сайт. `null` — файлы не загружены.
 */
export function useFacsimile(): { signature: string; stamp: string } | null {
  const query = useQuery({
    queryKey: ['facsimile'],
    queryFn: () => api.get<{ available: boolean; signature?: string; stamp?: string }>('/api/facsimile'),
    staleTime: Infinity,
  });
  const data = query.data;
  return data?.signature && data.stamp ? { signature: data.signature, stamp: data.stamp } : null;
}

/** Сохранённый прайс-лист из «Документы → Прайс-листы». */
export interface ApiPriceList {
  id: string;
  name: string;
  model: PriceListModel;
  createdAt: string;
  updatedAt: string;
}

export function usePriceLists(enabled = true) {
  return useQuery({
    queryKey: ['priceLists'],
    queryFn: () => api.get<{ priceLists: ApiPriceList[] }>('/api/price-lists'),
    enabled,
  });
}
